import { ref, toRefs, computed } from 'vue';
import events from '@/events';
import { Device, Call, TwilioError } from '@twilio/voice-sdk';

import util from '@/Utils/util';
import { useProfile } from '@/use/profile';
import { sendGet } from '@/Utils/api';
import { log } from '@/Utils/logger';
import { userInteractionService } from '@/services/userInteraction';

let twilioDevice: Device | null = null;
const isReconnecting = ref(false);
const canReconnectTwilio = ref(false);
let lastError: TwilioError.TwilioError | null = null;

export const useTwilio = () => {
  const { currentUser, userRoles } = useProfile();

  const {
    isAdmin,
    isSupervisor,
    isDirector,
    isTeamLead,
    isRideOperator,
    isGourmetOperator,
    isGroceryOperator,
    isLossPrevention,
    isQualityAssurance,
    isLossPreventionGourmet,
    isLossPreventionGrocery,
    isVirtualAssistanceGrocery,
  } = toRefs(userRoles);

  const isNeedTwilio = computed(() => {
    return (
      isAdmin.value ||
      isSupervisor.value ||
      isDirector.value ||
      isTeamLead.value ||
      isRideOperator.value ||
      isGourmetOperator.value ||
      isGroceryOperator.value ||
      isLossPrevention.value ||
      isLossPreventionGourmet.value ||
      isQualityAssurance.value ||
      isLossPreventionGrocery.value ||
      isVirtualAssistanceGrocery.value
    );
  });

  const getTwilioToken = async () => {
    try {
      const response = await sendGet('operatorClientToken', {
        operatorId: currentUser.value.id,
      });

      return response.data.token;
    } catch (error) {
      const errorText = util.getErrorResponse(error, false);
      log.error({ func: 'getTwilioToken', error }, errorText);
    }
  };

  const setupTwilioEventHandlers = () => {
    if (!twilioDevice) {
      return;
    }

    twilioDevice.on('registering', () => {
      log.info({ func: 'twilioDevice.onRegistering' }, 'Twilio device registering');
    });

    twilioDevice.on('registered', () => {
      canReconnectTwilio.value = false;
      events.emit('onTwilioReady', twilioDevice!);
    });

    twilioDevice.on('error', async (twilioError: TwilioError.TwilioError) => {
      log.error({ func: 'twilioDevice.onError', error: twilioError }, 'Twilio device error occurred');
      lastError = twilioError;
      events.emit('onTwilioError', twilioError);
    });

    twilioDevice.on('incoming', (call: Call) => events.emit('onTwilioIncoming', call));
    twilioDevice.on('tokenWillExpire', async () => {
      log.debug({ func: 'twilioDevice.onTokenWillExpire' }, 'Renewing Twilio token');
      if (!twilioDevice) {
        return;
      }

      const token = await getTwilioToken();
      twilioDevice.updateToken(token);
    });
    twilioDevice.on('destroyed', () => {
      log.info({ func: 'twilioDevice.onDestroyed' }, 'Twilio device destroyed');
    });
    twilioDevice.on('unregistered', () => {
      log.info({ func: 'twilioDevice.onUnregistered' }, 'Twilio device unregistered');
      canReconnectTwilio.value = true;
    });
  };

  const setupTwilioConfiguration = async () => {
    if (!isNeedTwilio.value) {
      return;
    }

    const token = await getTwilioToken();

    if (!token) {
      return;
    }

    try {
      twilioDevice = new Device(token, {
        enableImprovedSignalingErrorPrecision: true,
        sounds: {
          outgoing: '/static/silence.wav',
          incoming: '/static/silence.wav',
        },
      });

      if (!twilioDevice) {
        return;
      }

      setupTwilioEventHandlers();
      await twilioDevice.register();
    } catch (error) {
      log.error({ func: 'setupTwilioConfiguration', error }, 'Twilio setup failed');
    }
  };

  const reconnectTwilio = async () => {
    isReconnecting.value = true;

    try {
      userInteractionService.createUserInteraction({
        associatedObjectType: 'operator',
        associatedObjectId: currentUser.value.id,
        action: 'twilioReconnection',
        notes: 'Manual Twilio reconnection',
        changes: { lastError: lastError?.message },
      });
      lastError = null;

      if (!twilioDevice) {
        return;
      }

      // Just requesting a new token could leave the device in an inconsistent state (forever registering), leading to missed events.
      // Creating a new device is the only way to ensure the device is in a consistent state.
      twilioDevice.destroy();
      twilioDevice = null;
      await setupTwilioConfiguration();
    } catch (error) {
      log.error({ func: 'reconnectTwilio', error }, 'Twilio reconnection failed');
      throw error;
    } finally {
      isReconnecting.value = false;
    }
  };

  return {
    getTwilioToken,
    setupTwilioConfiguration,
    isReconnecting,
    canReconnectTwilio,
    reconnectTwilio,
  };
};
